/**
 * Cabinets — the monitored enclosures at a site, and Cratus's unit of onboarding.
 *
 * A cabinet BELONGS TO A SITE, because that is how everything else in the platform is scoped:
 * readings, alarms and access all hang from the site. Onboarding picks an existing site or
 * creates one in the same request, and the site keeps the location — region, sub-region, city,
 * coordinates — so two cabinets at one tower cannot disagree about where the tower is.
 *
 * `deviceName` is the controller that reports for the cabinet (the Cratus board, or a Thunder
 * Pulse gateway), as it names itself on the wire. `deviceId` is the device the API found by that
 * name at the site, or null until something by that name has reported.
 *
 * PICTURES AND BARCODE never travel through the API: the API hands out a short-lived signed
 * upload token, the browser sends the file straight to the stack's storage gateway, and the API
 * then confirms the object is there before the cabinet lists it. See Lumen-BE
 * `modules/cabinets/media.ts`.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { Platform } from '@/modules/cabinets/catalogue';
import { SUPABASE_URL } from '@/lib/supabase';
import { api, request } from './client';

export interface CabinetSite {
  id: string;
  name: string;
  region: string;
  subRegion: string | null;
  /** The site's type (Lumen-BE 0082): Greenfield, Rooftop, Indoor… Null until recorded. */
  siteType: string | null;
  city: string | null;
  lat: number | null;
  lng: number | null;
}

export interface CabinetListItem {
  id: string;
  serialNumber: string;
  /** The tenant's own reference for this cabinet — the operator it serves, on their numbering. */
  tenantRef: string | null;
  platform: Platform;
  /** A cabinet-type key — the platform's or the organisation's own (Lumen-BE 0077). */
  cabinetType: string | null;
  /** That type as the organisation names it. */
  cabinetTypeLabel: string | null;
  variantNo: string | null;
  deviceName: string | null;
  deviceId: number | null;
  /** The named controller's status at the device gate, or null until one has been heard (Lumen-BE). */
  controllerStatus: 'pending' | 'allowed' | 'blocked' | 'retired' | null;
  /**
   * The controller's OWN reporting state, from its newest stored reading on the site's clock —
   * not the site's, so a controller gone quiet at a site whose other devices report reads
   * `silent` here. `unnamed`: no controller named; `never`: named and never heard.
   */
  reporting: CabinetReporting;
  /** The controller's newest stored reading. Null when none has been stored. */
  lastReadingAt: string | null;
  /** Sensors fitted, activities included — an activity is a sensor of the Activities group. */
  sensorCount: number;
  site: CabinetSite;
  pictureCount: number;
  hasBarcode: boolean;
  createdAt: string;
  updatedAt: string;
  /** Bumped on every write; an edit sends it back as `If-Match`. */
  version: number;
}

/** `awaiting`: heard at the site, held at the device gate, so nothing it sends is kept yet. */
export type CabinetReporting = 'unnamed' | 'never' | 'awaiting' | 'reporting' | 'late' | 'silent';

export type MediaKind = 'picture' | 'barcode';

export interface CabinetMedia {
  id: string;
  kind: MediaKind;
  fileName: string | null;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
  /** A signed path on the storage gateway, valid for minutes — fetch it, do not store it. */
  signedPath: string;
}

/** A sensor the cabinet is fitted with, under the label it carries now. */
export interface FittedSensor {
  id: string;
  key: string;
  label: string;
  icon: string;
  groupLabel: string;
  section: FormSection;
  /** False once retired from the form — still shown, because the cabinet still has it. */
  active: boolean;
}

/** The cabinet's answer to one of its organisation's added fields. */
export interface FieldAnswer {
  id: string;
  key: string;
  label: string;
  dataType: FieldType;
  unit: string | null;
  value: string | number | boolean | string[];
  /** As a person reads it: a list's label, Yes/No, a number with its unit. */
  display: string;
  active: boolean;
}

export interface CabinetDetail extends CabinetListItem {
  sensors: FittedSensor[];
  fields: FieldAnswer[];
  media: CabinetMedia[];
}

/* ───────────────────────────────────────────── the form's catalogue, which an organisation extends */

export type FormSection = 'activities' | 'sensors';
export type SectionKind = 'pictures' | 'barcode' | 'basic' | 'activities' | 'sensors' | 'fields';
export type FieldType = 'text' | 'long_text' | 'number' | 'integer' | 'boolean' | 'date' | 'select' | 'multi_select';
export type BuiltInKey = 'serial_number' | 'tenant_ref' | 'platform' | 'site' | 'device_name' | 'cabinet_type' | 'variant_no';
export type OverrideKind = 'section' | 'group' | 'sensor' | 'field' | 'cabinet_type';

/** A section of the form: the platform's five, or a field section the organisation added. */
export interface FormSectionDef {
  id: string;
  key: string;
  label: string;
  /** The platform's label, for a built-in section — what Reset returns to. */
  defaultLabel: string | null;
  kind: SectionKind;
  builtIn: boolean;
  active: boolean;
  hidden: boolean;
  canHide: boolean;
  /** Cabinet type keys the section applies to; null is every type. */
  appliesTo: string[] | null;
  /** Pictures: how many a technician must add. */
  minCount: number;
  /** Barcode: whether one must be added. */
  required: boolean;
  sortOrder: number;
}

/** One of the seven built-in fields, as the organisation has adjusted it. */
export interface BuiltInFieldDef {
  key: BuiltInKey;
  label: string;
  defaultLabel: string;
  help: string | null;
  defaultHelp: string | null;
  required: boolean;
  alwaysRequired: boolean;
  canRequire: boolean;
  canHide: boolean;
  hidden: boolean;
  overridden: boolean;
}

export interface CabinetTypeDef {
  id: string;
  key: string;
  label: string;
  defaultLabel: string | null;
  builtIn: boolean;
  active: boolean;
  hidden: boolean;
  sortOrder: number;
}

export interface FormSensor {
  id: string;
  key: string;
  label: string;
  defaultLabel: string | null;
  icon: string;
  /** The platform's — on every organisation's form, adjusted only by override. */
  builtIn: boolean;
  active: boolean;
  hidden: boolean;
  sortOrder: number;
}

export interface FormSensorGroup {
  id: string;
  key: string;
  label: string;
  defaultLabel: string | null;
  section: FormSection;
  builtIn: boolean;
  active: boolean;
  hidden: boolean;
  appliesTo: string[] | null;
  /**
   * The activity (an Activities sensor, by key) that turns this group on; while it is off the
   * group's sensors cannot be fitted. Null: always on (Lumen-BE 0078).
   */
  activityKey: string | null;
  /** The platform's link, for a built-in group — what Reset returns to. */
  defaultActivityKey: string | null;
  sortOrder: number;
  sensors: FormSensor[];
}

export interface FormFieldOption {
  value: string;
  label: string;
  /** False once retired: still reads on the answers given, not offered for new ones. */
  active: boolean;
}

/** A field the organisation added. The built-in fields are `builtInFields`. */
export interface FormField {
  id: string;
  key: string;
  label: string;
  dataType: FieldType;
  sectionId: string;
  options: FormFieldOption[] | null;
  unit: string | null;
  help: string | null;
  placeholder: string | null;
  min: number | null;
  max: number | null;
  appliesTo: string[] | null;
  required: boolean;
  active: boolean;
  sortOrder: number;
  /** How many cabinets answered it — its kind of answer may change only while this is 0. */
  answered: number;
}

export interface CabinetForm {
  sections: FormSectionDef[];
  builtInFields: BuiltInFieldDef[];
  cabinetTypes: CabinetTypeDef[];
  groups: FormSensorGroup[];
  fields: FormField[];
}

export interface NewFormField {
  label: string;
  dataType: FieldType;
  sectionId?: string | undefined;
  options?: string[] | undefined;
  unit?: string | undefined;
  help?: string | undefined;
  placeholder?: string | undefined;
  min?: number | null | undefined;
  max?: number | null | undefined;
  appliesTo?: string[] | null | undefined;
  required: boolean;
}

/** Everything about an added field that may change. `null` clears. */
export interface FieldPatch {
  label?: string;
  dataType?: FieldType;
  sectionId?: string;
  unit?: string | null;
  help?: string | null;
  placeholder?: string | null;
  min?: number | null;
  max?: number | null;
  appliesTo?: string[] | null;
  required?: boolean;
  active?: boolean;
  /** The whole list of choices: existing ones by value, new ones without. */
  options?: Array<{ value?: string | undefined; label: string; active: boolean }>;
  addOptions?: string[];
}

/** One organisation's adjustment of a platform item. `null` returns that part to the default. */
export interface BuiltInPatch {
  label?: string | null;
  help?: string | null;
  hidden?: boolean;
  required?: boolean | null;
  minCount?: number | null;
  /** A built-in group's activity: a key, or null for "turned on by nothing". */
  activityKey?: string | null;
}

export type CabinetSiteInput =
  | { id: string }
  | {
      new: {
        name: string;
        region: string;
        subRegion?: string | undefined;
        /** One of the site catalogue's `site_type` choices (Lumen-BE 0082). */
        siteType?: string | undefined;
        city?: string | undefined;
        lat?: number | undefined;
        lng?: number | undefined;
      };
    };

export interface CreateCabinetInput {
  site: CabinetSiteInput;
  serialNumber: string;
  platform: Platform;
  tenantRef?: string | undefined;
  deviceName?: string | undefined;
  cabinetType?: string | undefined;
  variantNo?: string | undefined;
  /** Catalogue ids of every sensor fitted, activities included. */
  sensors: string[];
  /** Answers to the organisation's added fields, by field id. Blank answers are left out. */
  fields: Record<string, string | number | boolean | string[]>;
}

/**
 * An edit after onboarding — everything but the site (Lumen-BE `updateCabinetSchema`). An absent
 * built-in is unchanged and `null` clears it. `sensors`, when sent, is the whole set. `fields`
 * MERGES: an answer sets it, `null` removes it, a field left out is untouched.
 */
export interface UpdateCabinetInput {
  serialNumber?: string;
  tenantRef?: string | null;
  platform?: Platform;
  deviceName?: string | null;
  cabinetType?: string | null;
  variantNo?: string | null;
  sensors?: string[];
  fields?: Record<string, string | number | boolean | string[] | null>;
}

export interface CabinetFilters {
  siteId?: string | undefined;
  q?: string | undefined;
  limit?: number | undefined;
}

const KEY = ['cabinets'] as const;

export const cabinetKeys = {
  all: KEY,
  list: (f: CabinetFilters) => [...KEY, 'list', f.siteId ?? null, f.q ?? null, f.limit ?? null] as const,
  detail: (id: string) => [...KEY, 'detail', id] as const,
};

/**
 * Everything a cabinet write can change the answer to, in one place. `['sites']` because
 * onboarding may have created a site — every site picker and the site context read that key.
 */
function invalidateCabinetViews(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: KEY });
  void qc.invalidateQueries({ queryKey: ['sites'] });
  void qc.invalidateQueries({ queryKey: ['telemetry-health'] });
}

export function useCabinets(filters: CabinetFilters = {}, enabled = true) {
  return useQuery({
    queryKey: cabinetKeys.list(filters),
    enabled,
    queryFn: () =>
      api.get<{ data: CabinetListItem[]; nextCursor: string | null }>('/cabinets', {
        ...(filters.siteId ? { siteId: filters.siteId } : {}),
        ...(filters.q ? { q: filters.q } : {}),
        limit: filters.limit ?? 200,
      }),
    staleTime: 30_000,
  });
}

export function useCabinet(id: string | null) {
  return useQuery({
    queryKey: cabinetKeys.detail(id ?? ''),
    enabled: id !== null,
    queryFn: () => api.get<{ cabinet: CabinetDetail }>(`/cabinets/${id}`),
    /* The media paths are signed for minutes; a detail older than that has dead links. */
    staleTime: 60_000,
  });
}

export function useCreateCabinet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCabinetInput) => api.post<{ cabinet: CabinetDetail }>('/cabinets', input),
    onSuccess: () => invalidateCabinetViews(qc),
  });
}

/**
 * Edit a cabinet. `version` is what the editor read: a cabinet changed since is refused (409)
 * rather than overwritten.
 */
export function useUpdateCabinet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version, input }: { id: string; version: number; input: UpdateCabinetInput }) =>
      request<{ cabinet: CabinetDetail }>(`/cabinets/${id}`, {
        method: 'PATCH',
        body: input,
        headers: { 'If-Match': String(version) },
      }),
    onSuccess: (data) => {
      qc.setQueryData(cabinetKeys.detail(data.cabinet.id), data);
      invalidateCabinetViews(qc);
    },
  });
}

export function useDeleteCabinet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/cabinets/${id}`),
    onSuccess: () => invalidateCabinetViews(qc),
  });
}

/* ───────────────────────────────────────────────────────────── pictures and barcode ── */

/**
 * The API signs storage paths relative to the storage root, because the root THIS browser
 * reaches (through the gateway it signs in through) is not the one the API reaches. So the
 * console prefixes its own.
 */
const STORAGE_ROOT = `${SUPABASE_URL.replace(/\/+$/, '')}/storage/v1`;

/** The storage gateway refused a file, or could not be reached. Carries no server text. */
export class StorageUploadError extends Error {
  constructor(readonly status: number) {
    super(status === 0 ? 'The picture store could not be reached.' : `The picture store refused the file (${status}).`);
    this.name = 'StorageUploadError';
  }
}

/**
 * Uploads one picture or the barcode, in the API's three steps: the API issues a signed path
 * for exactly one object, the file goes STRAIGHT to storage (never through the API), and the
 * API then confirms what arrived before the cabinet lists it. The PUT starts at once, because
 * the signed path is only good for about a minute.
 */
export async function uploadCabinetMedia(cabinetId: string, kind: MediaKind, file: File): Promise<CabinetMedia> {
  const started = await api.post<{ mediaId: string; uploadPath: string }>(`/cabinets/${cabinetId}/media`, {
    kind,
    fileName: file.name,
    contentType: file.type,
    sizeBytes: file.size,
  });
  let res: Response;
  try {
    res = await fetch(`${STORAGE_ROOT}${started.uploadPath}`, {
      method: 'PUT',
      headers: { 'content-type': file.type },
      body: file,
    });
  } catch {
    throw new StorageUploadError(0);
  }
  if (!res.ok) throw new StorageUploadError(res.status);
  const done = await api.post<{ media: CabinetMedia }>(`/cabinets/${cabinetId}/media/${started.mediaId}/complete`, {});
  return done.media;
}

/**
 * A stored picture's bytes. Fetched rather than put in an `<img src>`, because the console's
 * CSP admits images from itself, `data:` and `blob:` only — never from the storage gateway —
 * and a blob URL made from these bytes is admitted.
 */
export async function fetchStoredImage(signedPath: string, signal?: AbortSignal): Promise<Blob> {
  const res = await fetch(`${STORAGE_ROOT}${signedPath}`, signal ? { signal } : {});
  if (!res.ok) throw new StorageUploadError(res.status);
  return res.blob();
}

const FORM_KEY = ['cabinet-form'] as const;

/**
 * The form as the organisation being worked in fills it in. `includeRetired` for the customise
 * view, which offers to restore what was retired; the form itself never shows it.
 */
export function useCabinetForm(includeRetired = false) {
  return useQuery({
    queryKey: [...FORM_KEY, includeRetired],
    queryFn: () => api.get<CabinetForm>('/cabinet-form', includeRetired ? { includeRetired: 'true' } : {}),
    staleTime: 60_000,
  });
}

/**
 * Changing the form, for holders of `cabinets.form.edit`. Every change re-reads both views of the
 * form, and the cabinet detail (a rename shows on the cabinets that carry the item).
 */
export function useCabinetFormEdits() {
  const qc = useQueryClient();
  const onSuccess = (): void => {
    void qc.invalidateQueries({ queryKey: FORM_KEY });
    void qc.invalidateQueries({ queryKey: [...KEY, 'detail'] });
  };
  return {
    createField: useMutation({
      mutationFn: (input: NewFormField) => api.post<{ field: FormField }>('/cabinet-form/fields', input),
      onSuccess,
    }),
    patchField: useMutation({
      mutationFn: ({ id, ...patch }: FieldPatch & { id: string }) => api.patch<{ field: FormField }>(`/cabinet-form/fields/${id}`, patch),
      onSuccess,
    }),
    createGroup: useMutation({
      mutationFn: (input: { label: string; section: FormSection; appliesTo?: string[] | null; activityKey?: string | null }) =>
        api.post<{ group: FormSensorGroup }>('/cabinet-form/groups', input),
      onSuccess,
    }),
    patchGroup: useMutation({
      mutationFn: ({ id, ...patch }: { id: string; label?: string; active?: boolean; appliesTo?: string[] | null; activityKey?: string | null }) =>
        api.patch<void>(`/cabinet-form/groups/${id}`, patch),
      onSuccess,
    }),
    createSection: useMutation({
      mutationFn: (input: { label: string; appliesTo?: string[] | null }) =>
        api.post<{ section: FormSectionDef }>('/cabinet-form/sections', input),
      onSuccess,
    }),
    patchSection: useMutation({
      mutationFn: ({ id, ...patch }: { id: string; label?: string; active?: boolean; appliesTo?: string[] | null }) =>
        api.patch<void>(`/cabinet-form/sections/${id}`, patch),
      onSuccess,
    }),
    createType: useMutation({
      mutationFn: (input: { label: string }) => api.post<{ type: CabinetTypeDef }>('/cabinet-form/types', input),
      onSuccess,
    }),
    patchType: useMutation({
      mutationFn: ({ id, ...patch }: { id: string; label?: string; active?: boolean }) =>
        api.patch<void>(`/cabinet-form/types/${id}`, patch),
      onSuccess,
    }),
    setBuiltIn: useMutation({
      mutationFn: ({ kind, key, ...patch }: BuiltInPatch & { kind: OverrideKind; key: string }) =>
        api.put<void>(`/cabinet-form/built-in/${kind}/${key}`, patch),
      onSuccess,
    }),
    resetBuiltIn: useMutation({
      mutationFn: ({ kind, key }: { kind: OverrideKind; key: string }) => api.del<void>(`/cabinet-form/built-in/${kind}/${key}`),
      onSuccess,
    }),
    setOrder: useMutation({
      mutationFn: (input: { kind: OverrideKind; ids: string[] }) => api.put<void>('/cabinet-form/order', input),
      onSuccess,
    }),
    createSensor: useMutation({
      mutationFn: (input: { groupId: string; label: string; icon: string }) =>
        api.post<{ sensor: FormSensor }>('/cabinet-form/sensors', input),
      onSuccess,
    }),
    patchSensor: useMutation({
      mutationFn: ({ id, ...patch }: { id: string; label?: string; icon?: string; active?: boolean; groupId?: string }) =>
        api.patch<void>(`/cabinet-form/sensors/${id}`, patch),
      onSuccess,
    }),
  };
}

export { invalidateCabinetViews };
